import { useState } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { Mail, User, LockKeyhole, Eye, EyeOff } from "lucide-react";
import { useToast } from "../context/ToastContext";
import {
  emailLooksValid,
  fieldInputInnerClassName,
  fieldInputShellClassName,
} from "../utils/formFieldClasses";

const SPRR_LOGO_SRC = "/logo.png";
const SPRR_LOGO_ALT = "SPRR Technologies Pvt Ltd";

function LoginCardBrand() {
  return (
    <div className="mb-6 flex justify-center bg-transparent sm:mb-8">
      <img
        src={SPRR_LOGO_SRC}
        alt={SPRR_LOGO_ALT}
        className="h-[8.75rem] w-auto max-w-[min(100%,420px)] bg-transparent object-contain"
      />
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name } = e.target;
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setForm((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const em = String(form.email || "").trim();
    const pw = String(form.password || "").trim();
    const staffEmailOk = emailLooksValid(em);
    const studentUsernameOk =
      !em.includes("@") &&
      em.length >= 3 &&
      em.length <= 48 &&
      /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(em);

    const next = {
      email: !em
        ? "Email (staff) or username (students) is required."
        : staffEmailOk || studentUsernameOk
          ? ""
          : "Staff: use your work email. Students: use the username (login ID) from your school, not your email.",
      password: !pw ? "Password is required." : "",
    };

    setFieldErrors(next);
    if (next.email || next.password) return;

    try {
      const res = await axios.post("/api/auth/login", form);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("role", res.data.role);

      if (res.data.role === "SUPER_ADMIN") navigate("/superadmin/dashboard");
      else if (res.data.role === "SCHOOL_ADMIN") navigate("/schooladmin/dashboard");
      else if (res.data.role === "TEACHER") navigate("/teacher/dashboard");
      else if (res.data.role === "STUDENT") navigate("/student/dashboard");
    } catch (err) {
      console.log(err);
      showToast(err.response?.data?.message || "Login failed", "error");
    }
  };

  const openForgotPassword = () => {
    setForgotError("");
    const em = String(form.email || "").trim();
    setForgotEmail(emailLooksValid(em) ? em : "");
    setShowForgotPassword(true);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError("");
    const em = String(forgotEmail || "").trim();

    if (!emailLooksValid(em)) {
      setForgotError("Enter the work email you use to sign in.");
      return;
    }

    try {
      setForgotSubmitting(true);
      const res = await axios.post(
        "/api/auth/forgot-password",
        { email: em },
        { skipGlobalLoader: true }
      );
      showToast(res.data?.message || "Check your email.", "success");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (err) {
      const status = err.response?.status;
      const msg =
        err.response?.data?.message ||
        (status === 503
          ? "Email is not set up on the server. Contact your administrator."
          : "Could not send reset email. Try again.");

      setForgotError(msg);
      if (status === 503) showToast(msg, "warning");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row overflow-y-auto">
      <div
        className="hidden lg:flex relative w-[55%] xl:w-[58%] overflow-hidden"
        style={{
          backgroundImage: "url('/login-bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#000]/30 via-[#000]/40 to-black/60" />
        <div className="relative z-10 flex flex-col justify-between w-full h-full px-10 xl:px-14 py-10 text-white">
          <div className="flex items-center justify-between gap-4 text-sm text-white/90">
            <span className="font-semibold tracking-wide">SPRR Technologies</span>
            <span className="shrink-0 rounded-full border border-white/35 px-3 py-2 text-xs font-medium text-white/90 backdrop-blur-sm">
              AI School Operating System
            </span>
          </div>

          <div className="max-w-[520px] mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs backdrop-blur">
              Built for Modern Schools
            </span>
            <h1 className="mt-3 text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight">
              One Platform.
              <br />
              Complete School
              <br />
              Management.
            </h1>
            <p className="mt-6 text-base xl:text-[16px] text-white leading-6">
              Manage students, teachers, admissions, academics, attendance and communication
              through one unified school operating system.
            </p>
          </div>

          <div className="flex justify-between text-xs text-white/80 font-medium">
            <p>© 2026 SPRR Technologies Pvt Ltd</p>
            <p>CBSE • ICSE • State Boards • Play School → Class 12</p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-y-auto bg-[#f6f7fb] px-4 py-10 sm:px-6 lg:py-0">
        <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-[0_18px_55px_rgba(15,23,42,0.08)] sm:p-9">
          {!showForgotPassword ? (
            <form onSubmit={handleLogin}>
              <LoginCardBrand />
              <h2 className="text-center font-[Manrope] text-3xl font-bold text-[#1e2a78] sm:text-4xl">
                Welcome Back
              </h2>
              <p className="mt-2 sm:mt-3 text-center text-[15px] text-slate-500">
                Staff sign in with email. Students use username and password.
              </p>

              <div className="mt-8 sm:mt-10 space-y-5 sm:space-y-6">
                <div>
                  <label htmlFor="login-email" className="text-sm font-semibold text-slate-800">
                    EMAIL OR USERNAME
                    <span className="text-red-500" aria-hidden>
                      {" "}
                      *
                    </span>
                  </label>
                  <div className={`mt-2.5 ${fieldInputShellClassName(Boolean(fieldErrors.email))}`}>
                    <div className="flex shrink-0 items-center gap-0.5 text-slate-400" aria-hidden>
            
                      <User size={14} className="-ml-1 opacity-70" />
                    </div>
                    <input
                      id="login-email"
                      name="email"
                      type="text"
                      inputMode="text"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="admin@school.edu or S12-A1B2C3"
                      autoComplete="username"
                      aria-invalid={Boolean(fieldErrors.email)}
                      aria-describedby={fieldErrors.email ? "err-login-email" : undefined}
                      className={fieldInputInnerClassName()}
                    />
                  </div>
                  {fieldErrors.email ? (
                    <p id="err-login-email" className="mt-1.5 text-xs font-medium text-red-600" role="alert">
                      {fieldErrors.email}
                    </p>
                  ) : null}
                </div>

                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label htmlFor="login-password" className="text-sm font-semibold text-slate-800">
                      PASSWORD
                      <span className="text-red-500" aria-hidden>
                        {" "}
                        *
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={openForgotPassword}
                      className="text-sm font-medium text-[#2b3896] underline-offset-2 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <div className={`mt-2.5 ${fieldInputShellClassName(Boolean(fieldErrors.password))}`}>
                    <LockKeyhole size={18} className="text-slate-400 shrink-0" />
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      aria-invalid={Boolean(fieldErrors.password)}
                      aria-describedby={fieldErrors.password ? "err-login-password" : undefined}
                      className={fieldInputInnerClassName()}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-slate-400 transition hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {fieldErrors.password ? (
                    <p id="err-login-password" className="mt-1.5 text-xs font-medium text-red-600" role="alert">
                      {fieldErrors.password}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-start gap-3">
                  <input type="checkbox" className="mt-1 h-4 w-4 rounded border-gray-300" />
                  <span className="text-sm leading-6 text-slate-500">Keep me logged in for 30 days</span>
                </div>

                <button
                  type="submit"
                  className="academic-gradient w-full rounded-[20px] py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_30px_rgba(43,56,150,0.24)] transition hover:opacity-95"
                >
                  Login to Dashboard
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <LoginCardBrand />
              <h2 className="text-center font-[Manrope] text-3xl font-bold text-[#1e2a78] sm:text-4xl">
                Reset password
              </h2>
              <p className="mt-2 sm:mt-3 text-center text-[15px] text-slate-500">
                For <strong>school staff</strong> (school admin, super admin, or teacher). Enter
                your work email.
              </p>

              <div className="mt-8 space-y-5 sm:mt-10 sm:space-y-6">
                <div>
                  <label htmlFor="forgot-email" className="text-sm font-semibold text-slate-800">
                    WORK EMAIL
                    <span className="text-red-500" aria-hidden>
                      {" "}
                      *
                    </span>
                  </label>
                  <div className={`mt-2.5 ${fieldInputShellClassName(Boolean(forgotError))}`}>
                    <Mail size={16} className="shrink-0 text-slate-400" aria-hidden />
                    <input
                      id="forgot-email"
                      type="email"
                      inputMode="email"
                      value={forgotEmail}
                      onChange={(e) => {
                        setForgotEmail(e.target.value);
                        setForgotError("");
                      }}
                      placeholder="you@school.edu"
                      autoComplete="email"
                      aria-invalid={Boolean(forgotError)}
                      aria-describedby={forgotError ? "err-forgot-email" : undefined}
                      className={fieldInputInnerClassName()}
                    />
                  </div>
                  {forgotError ? (
                    <p id="err-forgot-email" className="mt-1.5 text-xs font-medium text-red-600" role="alert">
                      {forgotError}
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  disabled={forgotSubmitting}
                  className="academic-gradient w-full rounded-[20px] py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_30px_rgba(43,56,150,0.24)] transition hover:opacity-95 disabled:opacity-70"
                >
                  {forgotSubmitting ? "Sending..." : "Send reset link"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotError("");
                  }}
                  className="w-full rounded-[20px] border-2 border-slate-200 py-3 text-[15px] font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
